import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Paper, Typography, Tooltip, Link, Chip } from '@mui/material';
import { VideoCall as VideoCallIcon } from '@mui/icons-material';
import {
  expandRecurringMeetings,
  taskToMeetingEvent,
  buildTimeSlots,
} from './calendarUtils';

/**
 * Day-timeline of meetings: Meetings-model entries (time-blocked at their
 * scheduled times) merged with meeting tasks. Entries without a time render as
 * all-day blocks above the timeline.
 *
 * @param {object} props
 * @param {dayjs.Dayjs} props.currentDate - The day being shown.
 * @param {Array<object>} props.events - Meetings-model entries from the API.
 * @param {Array<object>} props.tasks - Meeting tasks (category=Communication with a meeting-typed action item).
 */
const MeetingsPanel = ({ currentDate, events = [], tasks = [] }) => {
  const timeSlots = useMemo(() => buildTimeSlots(), []);

  // Expand recurring meetings, fold in meeting-tasks, keep only today's entries.
  const dayEntries = useMemo(() => {
    const expanded = expandRecurringMeetings(events);
    const taskEvents = tasks.map(taskToMeetingEvent);
    return [...expanded, ...taskEvents].filter((m) =>
      dayjs(m.start_date).isSame(currentDate, 'day'),
    );
  }, [events, tasks, currentDate]);

  const allDay = dayEntries.filter((m) => !m.start_time);
  const timed = dayEntries.filter((m) => m.start_time);

  const entriesForHour = (hour) =>
    timed.filter((m) => dayjs(`${m.start_date}T${m.start_time}`).hour() === hour);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        Meetings
      </Typography>

      {allDay.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {allDay.map((m) => (
            <Chip
              key={m.id}
              size="small"
              color={m.isTask ? 'secondary' : 'primary'}
              label={m.meeting_name}
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      )}

      {dayEntries.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No meetings for this day.
        </Typography>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '64px 1fr' }}>
        {timeSlots.map((time) => {
          const hourEntries = entriesForHour(time.hour());
          return (
            <React.Fragment key={time.format('HH')}>
              <Box
                sx={{
                  p: 0.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              >
                {time.format('HH:mm')}
              </Box>
              <Box sx={{ p: 0.5, borderTop: 1, borderColor: 'divider', minHeight: 36 }}>
                {hourEntries.map((m) => (
                  <Tooltip
                    key={m.id}
                    title={`${m.meeting_name} (${dayjs(`${m.start_date}T${m.start_time}`).format('HH:mm')}${
                      m.end_time ? ` - ${dayjs(`${m.start_date}T${m.end_time}`).format('HH:mm')}` : ''
                    })`}
                  >
                    <Box
                      sx={{
                        bgcolor: m.isTask ? 'secondary.main' : 'primary.main',
                        color: 'white',
                        p: 0.5,
                        borderRadius: 1,
                        mb: 0.5,
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {m.url ? (
                        <Link href={m.url} target="_blank" rel="noopener" color="inherit">
                          <VideoCallIcon fontSize="small" />
                        </Link>
                      ) : null}
                      {dayjs(`${m.start_date}T${m.start_time}`).format('HH:mm')} — {m.meeting_name}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

export default MeetingsPanel;
